<template>
  <div style="position: relative;">
    <div
      ref="viewer"
      class="viewer"
      :style="{ 'pointer-events': dragging ? 'none' : 'auto' }"
      @mousemove="$emit('mouse-move')" />
    <div
      class="viewer-overlay"
      :class="{ 'viewer-overlay-active': showOverlay }">
      <span class="viewer-overlay-message">
        <span v-show="overlayMessage">
          {{ overlayMessage }}<br><br>
        </span>
        <img :src="svgLoader" />
      </span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import WebViewer from '@pdftron/webviewer';
// const debounce = require('lodash/debounce');
// import wvScripting from '@/components/AppView/WebViewer/wvConfig.js'; // eslint-disable-line

/* eslint-disable */
export default {
  name: 'WebViewer',
  props: {
    dragging: Boolean,
    path: String,
  },
  data() {
    return {
      svgLoader: require('../../../assets/loader.svg'), // eslint-disable-line 
      displayOverlay: false,
      overlayMessage: null,
      importingAnnots: false,
      loadedDocument: null,
    };
  },
  computed: {
    ...mapState({
      openFile: state => state.files.openFile,
      requestedFile: state => state.files.requestedFile,
      webViewerFileData: state => state.files.webViewerFileData,
      userInfo: state => state.users.userInfo,
      openCards: state => state.appState.openCards,
      openItems: state => state.appState.openItems,
    }),
    ...mapGetters({
      getCardRef: 'cards/getCardRef',
    }),
    requestIsBeingProcessed() {
      // Second condition is to make sure this gives back the correct value. It has sometimes occurred that the overlay remains even though the doc must already have been loaded,
      // which must be due to this one claiming the request is being processed (i.e. returning true). Additional check verifies whether we are on the page of the openFile and whether
      // that file is already in fileData, in which case the request is clearly done and part after the && will return false, making the whole condition false independent of the part
      // before the &&. Question then is only why we do not just use the second condition (right?).
      return this.requestedFile !== this.openFile && !(this.openFile === this.cardId && this.fileData.doc);
    },
    showOverlay() {
      return this.requestIsBeingProcessed || this.displayOverlay;
    },
    fileData() {
      return this.webViewerFileData.find(entry => entry.cardId === this.openFile);
    },
    cardId() {
      return Number(this.$route.params.cardId);
    },
    currentCard() {
      const fileData = this.webViewerFileData.find(entry => entry.cardId === this.cardId);
      if (fileData) return fileData.ref;
      return {};
    },
  },
  methods: {
    ...mapActions({
      processAnnotations: 'files/processAnnotations',
      addWebViewerFileData: 'files/addWebViewerFileData',
      openInWebViewer: 'files/openInWebViewer',
      persistAll: 'sync/persistAll',
      replaceFile: 'files/replaceFile',
    }),
    ...mapMutations({
      UpdateWebViewerFileData: 'files/UpdateWebViewerFileData',
      ResetRequestedAndOpenFile: 'files/ResetRequestedAndOpenFile',
    }),
  },
  mounted() {
    WebViewer({
      path: '/webviewer', // this.path,
      fullAPI: true,
      isAdminUser: true, // allows editing all annotations
      annotationUser: this.userInfo.name || this.userInfo.firstName || this.userInfo.email.slice(0, this.userInfo.email.indexOf('@')),
    }, this.$refs.viewer)
      .then(async (instance) => {
        instance.setHeaderItems((header) => {
          const goToCardButton = {
            type: 'actionButton',
            img: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsQAAA7EAZUrDhsAAAEjSURBVGhD7ZixisJAFEXj9q6svfWW+hXaLgjuh0hgf0L8j1WwtrC23dZaWxHXD9D7yBijvhQxwZsx78DhDanmwCQDqYVhGPjMm5veYgFsLICNBbBJC2jBKfyHR7Kyhxn8hHdoAbL5P9iHdXlARvbwBZdQ9naFFjCGzWhZKj7gKFpe0AK6bpaRnpsxWsDtsamRTfLuZkzaS+wNFsDGAtg8EqDdlkWaCTtCbCoZoN2WRZoJO0JsKhmgfbvzmAs7QmwqGaB9u/OYCztCbCyAzUsGHNw8o92ezzTJ3s0YLWDhZhmZuxmjBfzAXbQsFVs4jJYXtIAVbMMJlF/bbGQPv7ADN/IgSdpLvIYD2IDa7flMZQ/f8G7zQlqAN1gAGwtgYwFcguAEfqCDv9aV2MEAAAAASUVORK5CYII=',
            title: 'Go to card',
            onClick: () => {
              const { id, relatedCollectionId } = this.currentCard;
              this.$goToCard({ id, relatedCollectionId });
            }
          }
          header.push(goToCardButton);
        });

        const { docViewer, annotManager, PDFNet } = instance;
        const iframeDoc = instance.iframeWindow.document;

        // Try fix disable anti alias
        // await PDFNet.initialize()
        // const PDFDraw = new PDFNet.PDFDraw()
        // console.log('PDFDraw :>> ', PDFDraw);
        // await PDFDraw.setAntiAliasing(false);
        // ==> Get error message 'value is undefined'. Check with PDFtron how this should be done.

        // Fix some CSS
        iframeDoc.querySelector('.content').style.borderLeft = '1px solid #dbdbdb';
        iframeDoc.querySelector('.Header').style.height = '46px';

        // On instantiation, check whether we need to open a file (if we navigated directly to ItemView)
        if (this.$route.name === 'WebViewer' && !docViewer.getDocument()) {
          const cardId = Number(this.$route.params.cardId);
          const card = this.getCardRef({ cardId });
          if (!card.item || card.item.type !== 'file') this.$goToCard(card);
          await this.openInWebViewer(card);

          const { ref, doc } = this.webViewerFileData.find(entry => entry.cardId === cardId);
          instance.loadDocument(doc, { filename: ref.item.address });
        }

        // On instantiation, check whether there are pinnedItems that we can already add to webViewerFileData (except the one we are already requesting)
        this.openItems.forEach((openItem) => {
          const { item, pinned } = openItem;
          if (!pinned) return; // Do not load non-pinned cards

          if (item.item && item.item.type === 'file' && item.id !== this.requestedFile) {
            this.addWebViewerFileData(item);
          }
        });

        // Set up watcher for determining when to load a new file
        this.$watch('openFile', function (newVal, oldVal) {
          if (this.importingAnnots) {
            this.displayOverlay = true;
            this.overlayMessage = 'Annotations of this document are processed, cannot switch to another document yet!';
            return;
          }

          if (oldVal && newVal !== oldVal) {
            // If we had a document loaded, we should store the current viewState
            const zoomLevel = instance.getZoomLevel();
            const scrollElement = docViewer.getScrollViewElement();
            const { scrollLeft, scrollTop } = scrollElement;
            this.UpdateWebViewerFileData({
              cardId: oldVal,
              scrollLeft,
              scrollTop,
              zoomLevel,
            });
          }

          // Then load the new document
          if (newVal && newVal !== oldVal) {
            const { ref, doc } = this.webViewerFileData.find(entry => entry.cardId === newVal);
            instance.loadDocument(doc, { filename: ref.item.address });
          }
        });

        // Watcher for closing files that are removed from webViewerFileData (for when cards or items get deleted)
        // Exception for when importing in-file annotations, since that is just a delete and reupload of the cleaned up file. Doc can be kept in WebViewer then.
        this.$store.subscribe((mutation) => {
          if (mutation.type === 'files/RemoveFromWebViewerFileData' && !this.importingAnnots) {
            const cardId = mutation.payload;

            if (this.openFile === cardId) {
              docViewer.closeDocument();
              this.ResetRequestedAndOpenFile();
            }
          }
        });

        // Helper: restore view state
        const restoreViewState = function({ scrollLeft, scrollTop, zoomLevel }) {
          if ((!scrollLeft && scrollLeft !== 0) || (!scrollTop && scrollTop !== 0) || !zoomLevel) return;

          instance.setZoomLevel(zoomLevel);
          docViewer.scrollTo(scrollLeft, scrollTop);
        };

        // Helper: construct xfdf strings
        const constructXfdfString = (annot) => {
          let xfdfString = '<?xml version="1.0" encoding="UTF-8" ?>';
          xfdfString += '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">';
          xfdfString += annot;
          xfdfString += '</xfdf>';

          return xfdfString;
        }

        // WEBVIEWER EVENT HANDLERS
        docViewer.on('documentLoaded', async () => {
          this.loadedDocument = this.cardId; // Used to check for which document the annotationsLoaded event is actually fired (since that one fires on unloadingDocs as well...)
          // If in-file annotations have not been processed yet (i.e. deleted from file and saved to db), do not import annots yet (annotationsLoaded handler will process this further)
          if (!this.fileData.ref.item.inFileAnnotationsProcessed) return;

          // Import annotations, if there are any
          const { annots } = this.fileData;
          if (annots.length) {
            annots.forEach((annotation) => {
              annotManager.importAnnotCommand(annotation.xfdfString);
            });
          }

          // If we have a view state stored, restore that
          if (this.fileData.zoomLevel) {
            const { scrollLeft, scrollTop, zoomLevel } = this.fileData;
            restoreViewState({ scrollLeft, scrollTop, zoomLevel });
          }
        });

        docViewer.on('annotationsLoaded', async () => {
          if (!this.fileData || this.fileData.ref.item.inFileAnnotationsProcessed || this.loadedDocument !== this.cardId) {
            // Either the annotations are already processed, or this annotationsLoaded event is only fired because we unloaded a document. In both cases, we should not
            // try to export/import annotations (save to db instead of file).
            // The first !this.fileData is for cases when you close the last item in fileData, then I sometimes get an error message because this.fileData is undefined.
            return;
          }
          // Weird: WebViewer throws a warning saying that I call exportAnnotations (below, in getXfdfArray) before all annotations were loaded. This despite all this happening
          // after the annotationsLoaded event. Waiting for 0.3 seconds before executing the rest prevents that warning (did not test less than that), but it does not seem to
          // matter for actual functionality.

          const inFileAnnots = annotManager.getAnnotationsList();

          // Save annotations to db, delete from document
          this.importingAnnots = true; // Indicator for ignoring annotationChanged event and for not opening new documents while this is busy (can corrupt pdf doc)

          if (!inFileAnnots.length) { // No annotations that we need to save to the db
            this.$updateCard({
              id: this.fileData.ref.id,
              data: {
                item: Object.assign({}, this.fileData.ref.item, { inFileAnnotationsProcessed: true }),
              },
            });

            this.displayOverlay = false;
            this.overlayMessage = '';
            this.importingAnnots = false;
            return;
          }

          // exportAnnotations returns a promise, bit annoying here since I need to do a loop. Fixed that with this helper function.
          const getXfdfArray = () => {
            const xfdfArray = [];
            return new Promise((resolve, reject) => {
              inFileAnnots.forEach(async (annot, index) => {
                const xfdf = await annotManager.exportAnnotations({
                  annotList: [
                    annot,
                  ],
                  widgets: false,
                  links: false,
                  fields: false,
                });

                xfdfArray.push(xfdf);
                if (xfdfArray.length === inFileAnnots.length) resolve(xfdfArray);
              });
            });
          }
          const inFileAnnotList = await getXfdfArray();
          const annotsToDelete = [];
          inFileAnnotList.forEach((xfdf, index) => {
            // Since I'm not exporting links, widgets, and fields, xfdf may be undefined. Skip, in that case.
            if (!xfdf) return;

            const annot = inFileAnnots[index];
            // Instead of exportAnnotations, you can also use exportAnnotCommand, which has the advantage that <add> / <modify> / <delete> is already
            // specified. Used that first, but later changed to exportAnnotations since there is no way (it seems) to use exportAnnotCommand for handling annotations
            // first time a file is opened (moving in-file annotations to the db).
            // Because we use exportAnnotations, we need to specify <add>...</add> when constructing the xfdf string.
            const regex = /(?:<annots>)([.\s\S]*)(?:<\/annots>)/gm;
            const [matches] = [...xfdf.matchAll(regex)];

            if (!matches) return; // If no matching annotation, leave it in the document (an internal link does not give matches, e.g.)

            // Construct xfdf string and process.
            const xfdfString = constructXfdfString(`<add>${matches[1]}</add>`); // 2nd element is without the non-capturing groups
            const regexAnnotId = /(?:name=")([^\s]+)(?:")/g;
            const [matchesId] = [...xfdf.matchAll(regexAnnotId)];
            if (!matchesId) {
              // For some reason no matches id. Log to check which annotation, then return
              // console.log('Warning: matchesId is falsey')
              // console.log('xfdf :>> ', xfdf);
              // console.log('xfdfString :>> ', xfdfString);
              return
            }
            const annotationId = matchesId[1];
            // Use processAnnotations to add annots to fileData, delete annotation from annotationManager (will be reimported later, see below)
            this.processAnnotations({
              cardId: this.fileData.ref.id,
              relatedCollectionId: this.fileData.ref.relatedCollectionId,
              annotationId,
              xfdfString,
              action: 'add',
            });
            annotsToDelete.push(annot);
          });
          annotManager.deleteAnnotations(annotsToDelete, true, true);

          // Load the annotations again (if any)
          const { annots } = this.fileData;
          if (annots.length) {
            const promises = [];
            annots.forEach((annotation) => {
              promises.push(annotManager.importAnnotCommand(annotation.xfdfString));
            });
            const promisesResults = await Promise.all(promises);
            // Import is done, but annots still need to be drawn in the doc
            const annotationsToDraw = promisesResults.map(promise => promise[0]);
            annotManager.drawAnnotationsFromList(annotationsToDraw);
          }

          // Upload file without annotations
          const { id: cardId, relatedCollectionId, item } = this.fileData.ref;
          const doc = docViewer.getDocument();
          const fileData = await doc.getFileData({
            xfdfString: constructXfdfString(''),
          });
          const dataView = new DataView(fileData);
          const blob = new Blob([dataView], { type: 'application/pdf' });
          // Putting it in formData is necessary, else no boundary gets added to the file data
          const formData = new FormData()
          formData.append('card-item', blob);
          await this.replaceFile({ cardId, relatedCollectionId, filename: item.address, blob: formData });

          // Indicate that this check is done
          this.$updateCard({
            id: this.fileData.ref.id,
            data: {
              item: Object.assign({}, this.fileData.ref.item, { inFileAnnotationsProcessed: true }),
            },
          });

          this.displayOverlay = false;
          this.overlayMessage = '';
          this.importingAnnots = false;
        });

        annotManager.on('annotationChanged', async (annotations, action, info) => {
          if (info.imported || this.importingAnnots) return;

          const [annotation] = annotations;
          // See comment in 'annotationsLoaded' handler above on exportAnnotations vs exportAnnotCommand
          // Constructing xfdfStrings for deletes is not necessary right?
          const xfdf = await annotManager.exportAnnotations({
            annotList: [
              annotation,
            ],
          });
          const regex = /(?:<annots>)([.\s\S]*)(?:<\/annots>)/gm;
          const [matches] = [...xfdf.matchAll(regex)];
          // Construct xfdf string and process.
          const xfdfString = constructXfdfString(`<add>${matches[1]}</add>`); // 2nd element is without the non-capturing groups
          const { id: cardId, relatedCollectionId } = this.fileData.ref;

          this.processAnnotations({ cardId, relatedCollectionId, annotationId: annotation.Id, xfdfString, action });
        });

        // TEST COPY
        instance.iframeWindow.addEventListener('copy', (e) => {
          const page = docViewer.getCurrentPage();
          let text = docViewer.getSelectedText(page);
          let textQuads = docViewer.getSelectedTextQuads(page);
          // text = text.replace(/(?!\n\n)\r?\n|\r/g, ' ');
          text = text.replace(/(\r?\n|\r)(?!\r?\n|\r)/g, ' '); // gets rid of newlines, but also of paragraphs and other stuff...

          e.clipboardData.setData('text/plain', text);
          e.preventDefault();
        });

      });
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/layouts';
@import '~styles/colors';

.viewer {
  height: 100%;

  ::v-deep iframe {
    height: calc(100% - 0.35vh);
    width: 100%;
  }
}
.no-mouse-events {
  ::v-deep iframe {
    pointer-events: none;
  }
}

.viewer-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  margin-top: 47px;
  background-color: #dbdbdb;
  opacity: 0;
  transition: opacity 0.2s;
}
.viewer-overlay-active {
  opacity: 1;
  width: 100%;
  height: calc(100% - 47px);
}
.viewer-overlay-active .viewer-overlay-message {
  display: block;
}
.viewer-overlay-message {
  display: none;
  font-size: 1.5rem;
  font-weight: 700;
  color: $primary-dark;
  letter-spacing: 0.25rem;
  width: 100%;
  text-align: center;
  padding-top: 20vh;
}
</style>
